import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  currentOrgSelector,
  currentUserSelector,
  jwtSelector,
} from "../state/selectors/authSelector";

import { AppDispatch, RootState, store } from "../state/store";
import { setShouldRerender } from "../../features/graph/state/graphuSlice";
import { selectGraphContentByIds } from "../../features/graph/state/graphLegacy";
import { InitEventSource, getEventSource, IEventMessage } from "../util/EventSource";

import CreatorDashboard from "../../pages/CreatorDashboard/CreatorDashboard";
import Releases from "../../pages/Releases/Releases";
import { EULA } from "../../pages/EULA";
import { Help } from "../../pages/Help";
import { Login, ForgottenPassword, SetPassword } from "../../pages/Login";
import { DownloadClient } from "../../pages/DownloadClient";
import { DeviceManager } from "../../pages/DeviceManager";
import PublicDownloadPage from "../../pages/PublicDownload/PublicDownloadPage";

import Layout from "./Layout";
import { ProjectsList, Templates, Main, ProjectEdit } from "../../pages/Projects";

import ResetPassword from "../../pages/Login/ResetPassword";
import {
  StatisticsLayout,
  GlobalView,
  ParcoursListView,
  ParcoursDetailView,
  ModuleView,
  ApprenantsListView,
  ApprenantDetailView,
  ClassementView,
  ComparateurView,
  ReportsListView,
  ReportsFormView,
} from "../../pages/StatisticsNew";
import { Organization, ProfileMain, ProfileWrapper, Subscription } from "../../pages/Profile";

import { NodeScreen, ProjectGraph } from "../../pages/Projects/ProjectGraph";
import {
  //getEditorWebLastCommit,
  logoutAuth,
} from "../../features/auth/state/authSlice";
import { BackgroundMusic } from "../../features/graph/components/BackgroundMusic";
import { ProjectGroupsList } from "../../pages/ProjectGroups";
import CreateProjectGroupWrapper from "../../pages/ProjectGroups/CreateProjectGroupWrapper";
import CreateProjectWrapper from "../../pages/Projects/CreateProjectWrapper";
import ProjectGroupEdit from "../../pages/ProjectGroups/ProjectGroupEdit";
import ProjectGroupPublish, {
  PROJECT_GROUP_PUBLISH_COMPONENT_ID,
} from "../../pages/ProjectGroups/ProjectGroupPublish";
import ProjectGroupInformation from "../../pages/ProjectGroups/ProjectGroupInformation";
import ProjectInfo from "../../pages/Projects/ProjectInformation";

import AskPublicationWrapper from "../../features/projectGroupPublication/components/AskPublicationWrapper";
import { CreateNewScene } from "../../features/graph/components/CreateNewScene";
import EditProjectNameWrapper from "../../pages/Projects/EditProjectNameWrapper";
import FaitTemplateWrapper from "../../features/faitTemplate/components/FaitTemplateWrapper";
import EditProjectGroupNameWrapper from "../../pages/ProjectGroups/EditProjectGroupNameWrapper";
import {
  fetchAnonymousSessionsCount,
  fetchPaEmbedLinks,
  fetchSubscription,
  getSubscriptionStatus,
  setSubscriptionState,
} from "../../features/subscription/state/subscriptionSlice";
import NoSubscriptionHome from "../../pages/NoSubscriptionHome/NoSubscriptionHome";
import ErrorView from "../components/ErrorBoundary/ErrorView";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import { clearAlerts, setAlert } from "../../features/alert/state/alertsSlice";
import { Gallery } from "../../features/gallery/Gallery";
import ZendeskChat from "../components/ZendeskChat/ZendeskChat";
import { MobileDashboard } from "../../mobilePages/MobileDashboard";
import { MobileUploadSelectedFile } from "../../mobilePages/MobileUploadSelectedFile";
import { MobileUploadsList } from "../../mobilePages/MobileUploadsList";
import MobileLayout from "./MobileLayout";
import EnduserProjectGroupsList from "../../enduserPages/EnduserProjectGroupsList/EnduserProjectGroupsList";
import EnduserProjectsList from "../../enduserPages/EnduserProjectsList/EnduserProjectsList";
import {
  EnduserStatsLayout,
  EnduserStatsOverviewPage,
  EnduserParcoursListView,
  EnduserParcoursDetailView,
  EnduserModuleDetailView,
  EnduserSessionDetailView,
  EnduserGazeHeatmapView,
  EnduserComparateurView,
} from "../../pages/EnduserStats";
import { isStringNullOrEmpty } from "../util/isStringNullOrEmpty";
import ProjectParticipants from "../../pages/Projects/ProjectParticipants";
import ProjectGroupCollaborators from "../../pages/ProjectGroups/ProjectGroupCollaborators";
import ProjectGroupLearnersWrapper from "../../pages/ProjectGroups/ProjectGroupLearnersWrapper";
import ProjectGroupLearnersList from "../../pages/ProjectGroups/ProjectGroupLearnersList";
import ProjectGroupLearnersInvite from "../../pages/ProjectGroups/ProjectGroupLearnersInvite";
import ProjectGroupLearnersUpdate from "../../pages/ProjectGroups/ProjectGroupLearnersUpdate";
import PublishNewVersion from "../../features/projectGroupPublication/components/PublishNewVersion";
import DuplicateProjectWrapper from "src/features/duplicateProject/components/DuplicateProjectWrapper";
import DuplicateProjectGroupWrapper from "src/features/duplicateProjectGroup/components/DuplicateProjectGroupWrapper";
import { getFromLocalStorage, storeInLocalStorage } from "../util/localStorage";
import detectIfMobileBrowser from "../util/DetectIfMobileBrowser";
import { GlobalModals } from "../components/GlobalModals/GlobalModals";
import { productionMode } from "./App";
import { fetchBuilds } from "src/features/builds/state/buildsSlice";

import {
  CollaboratorsList,
  CollaboratorsWrapper,
  CreateCollaborator,
  UpdateCollaborator,
} from "src/pages/Collaborators";
import {
  CreateParticipant,
  NewParticipants,
  ParticipantsCsvImport,
  ParticipantsList,
  ParticipantsWrapper,
  UpdateParticipant,
} from "src/pages/Participants";
import {
  getMedia,
  setBlockadeLabsImagePreview,
  setUploadHasBeenTranscribed,
} from "src/features/gallery/state/gallerySlice";
import { getAllSessionsByProjectGroup } from "src/features/stats/state/statsSlice";
import { STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID } from "src/features/stats/components/StatsOverview/StatsOverview";
import { getAllProjects, setCurrentProjectFromId } from "src/features/projects/state/projectsSlice";
import TemplatesWrapper from "src/pages/Templates/TemplatesWrapper";
import TemplatesList from "src/pages/Templates/TemplatesList";
import ThemesWrapper from "src/pages/Themes/ThemesWrapper";
import ThemesList from "src/pages/Themes/ThemesList";
import SpecificTemplateWrapper from "src/pages/Templates/SpecificTemplateWrapper";
import ChatWrapper from "src/pages/Chat/ChatWrapper";
import ScenarioHelperChat from "src/pages/Chat/ScenarioHelperChat";
import WixarpediaChat from "src/pages/Chat/WixarpediaChat";
import AgentsWrapper from "src/pages/Agents/AgentsWrapper";
import AgentList from "src/pages/Agents/AgentList";
import AgentDetail from "src/pages/Agents/AgentDetail";
import AgentForm from "src/pages/Agents/AgentForm";
import DocumentsList from "src/pages/Agents/DocumentsList";
import { ANONYMOUS_SESSION_COUNTER_COMPONENT } from "src/features/subscription/components/AnonymousSessionsCounter";
import EditProjectGroupThemeWrapper from "src/pages/ProjectGroups/EditProjectGroupThemeWrapper";
import EditProjectGroupThumbnailWrapper from "src/pages/ProjectGroups/EditProjectGroupThumbnailWrapper";
import EditProjectGroupImageWrapper from "src/pages/ProjectGroups/EditProjectGroupImageWrapper";
import EditProjectThumbnailWrapper from "src/pages/Projects/EditProjectThumbnailWrapper";
import EditProjectLanguageWrapper from "src/pages/Projects/EditProjectLanguageWrapper";
import HelpCenter from "src/pages/Help/HelpCenter";
import PublishSameVersionOnNewPlatformsWrapper from "src/features/projectGroupPublication/components/PublishSameVersionOnNewPlatformsWrapper";
import ProjectGroupMedias from "src/pages/ProjectGroups/ProjectGroupMedias";
import ProjectMedias from "src/pages/Projects/ProjectMedias";
import PreviewPage from "src/pages/Preview/PreviewPage";

const HEARTBEAT_TIMEOUT_MS = 30000;

const Router = () => {
  const jwt = useSelector(jwtSelector);
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  // const isAdmin = useSelector(getIsAdminOfOrganization);
  const isSubscriptionRunning = subscriptionStatus === "Active";

  const [screenSize, setScreenSize] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  // Timestamp of the last SSE heartbeat. A ref (not a per-render `let`) so the
  // watchdog and the message handler share one value across renders.
  const lastHeartbeatRef = useRef(Date.now());
  // Coalesces the burst of per-content 302 events one relink/delink emits into a
  // single graph reload.
  const graphReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TODO: handle Mobile-landscape separately for style purposes
  // const [isInLandscapeMode, setLandscapeMode] = useState(
  //   window.matchMedia("(orientation: landscape)").matches,
  // );

  const isMobile = detectIfMobileBrowser();

  // Track whether we've handled the initial page-load redirect.
  // On page refresh, Redux state (selected project group, current project, etc.)
  // is lost. Routes with dynamic params (e.g. /courses/123/list) would render
  // with empty state, causing broken UI and "Server error" alerts.
  const hasHandledRefreshRedirect = useRef(false);

  // SSE catch-up: the first successful stream open is the initial connect;
  // every later `onopen` is a RECONNECT. While the stream was down/stalled it
  // may have missed events (Redis pub/sub has no replay), so on reconnect we
  // refetch the SSE-driven state to self-heal instead of leaving stale UI.
  const hasOpenedSseRef = useRef(false);

  useEffect(() => {
    dispatch(clearAlerts());
    // Only redirect authenticated users away from unauthenticated routes
    if (jwt) {
      // Note: /set-password should be accessible even when logged in (Story 21.5)
      // The page validates the token and handles logged-in users appropriately
      const unauthRoutes = ["/login", "/forgottenpassword", "/reset-password"];
      if (unauthRoutes.includes(window.location.pathname)) {
        navigate("/");
      }
    }
  }, []);

  const { user, organization } = useSelector((state: RootState) => {
    return {
      user: currentUserSelector(state),
      organization: currentOrgSelector(state),
    };
  });

  const checkNewCommit = (lastCom: string) => {
    const currentCommitHash = getFromLocalStorage("CURRENT_COMMIT_HASH");
    if (lastCom !== currentCommitHash && !isStringNullOrEmpty(lastCom)) {
      storeInLocalStorage("CURRENT_COMMIT_HASH", lastCom);
      window.location.href = `${window.location.origin}/?nocache=${new Date().getTime()}`;
    }
  };

  // SSE: create EventSource and attach handlers in the same flow.
  // Both jwt and user.id are needed: jwt for the SSE auth param,
  // user.id for message routing (disconnect, etc.)
  useEffect(() => {
    if (!jwt || !user?.id) return undefined;

    const handleSseOpen = () => {
      // Re-arm the watchdog on every (re)open so a fresh stream isn't flagged
      // stale before its first heartbeat arrives.
      lastHeartbeatRef.current = Date.now();
      if (hasOpenedSseRef.current) {
        // Reconnect after a drop/stall — recover anything missed meanwhile.
        runSseCatchUp();
      } else {
        hasOpenedSseRef.current = true;
      }
    };
    const handleSseError = (e: any) => console.error("SSE error: ", e);

    const es = getEventSource();
    if (es && es.readyState !== EventSource.CLOSED) {
      // Already connected — just re-attach handlers (e.g. if user.id changed)
      es.onopen = handleSseOpen;
      es.onmessage = handleSSEMessage;
      es.onerror = handleSseError;
    } else {
      InitEventSource(jwt, {
        onopen: handleSseOpen,
        onerror: handleSseError,
        onmessage: handleSSEMessage,
      });
    }

    // Stall watchdog. A half-open ("zombie") socket or a Redis-subscriber
    // reconnect can leave the stream silently dead: the browser sees no error,
    // so it never auto-reconnects, and any event meanwhile is lost (no replay).
    // If no heartbeat (server sends one every ~10s) arrives within the timeout,
    // force a fresh EventSource — InitEventSource closes the old one first, and
    // the reopen fires handleSseOpen → runSseCatchUp(). We deliberately do NOT
    // reload the page here (that caused WIXAR-3303).
    const watchdog = setInterval(() => {
      if (!jwt) return;
      if (document.visibilityState !== "visible") {
        // Background tabs throttle timers; don't fight that or false-trigger.
        // Re-arm so we start clean when the tab regains focus.
        lastHeartbeatRef.current = Date.now();
        return;
      }
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        lastHeartbeatRef.current = Date.now();
        InitEventSource(jwt, {
          onopen: handleSseOpen,
          onerror: handleSseError,
          onmessage: handleSSEMessage,
        });
      }
    }, 10000);

    return () => {
      clearInterval(watchdog);
      const current = getEventSource();
      if (current) current.close();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, user?.id]);

  // Refetch SSE-driven state that may have changed while the stream was
  // disconnected or stalled. Kept context-free (no page-specific args) so it
  // is safe to fire on any reconnect; thunks no-op visibly when their view
  // isn't mounted. Mirrors the refetches done by the SSE message handlers.
  function runSseCatchUp() {
    dispatch(fetchBuilds({ componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID }));
    dispatch(fetchSubscription({}));
  }

  function handleSSEMessage(event: MessageEvent) {
    const eventMessage = JSON.parse(event.data) as IEventMessage;
    const currentUserId = Number(user?.id);

    const disconnect = () => {
      alert(`${t("disconnected")}`);
      dispatch(logoutAuth());
    };

    if (!productionMode && eventMessage.action !== "001_HEARTBEAT") {
      console.warn("SSE received: ", eventMessage);
    }

    /*
      SSE CODE:
        - 0** : PING
        - 1** : LOGIN/LOGOUT
        - 2** : DATABASE INSERT
        - 3** : DATABASE UPDATE
        - 4** : DATABASE DELETE
    */
    switch (eventMessage.action) {
      case "101_DISCONNECT_ONE": {
        const environmentId = eventMessage.issuedFromEnv;

        if (Number(eventMessage?.issuedFromUser) === currentUserId) {
          if (
            Number(environmentId) === 2 && // hardcoded-value for environment.id 2 (should be "WebApp" in DB)
            !isMobile
          ) {
            // The native alert() here is deliberate, not an oversight. Before replacing it with a
            // custom modal, read this article and its comments — replacing it is harder than it
            // looks: https://css-tricks.com/evil-please-dont-modals-overlays-edition/

            // 8 Novembre 2023: cette feature est trop clunky, je la désactive le temps du rework SSE (redis)
            // console.log("I should call ", disconnect);
            disconnect();
          }
          if (Number(environmentId) === 4 && isMobile) {
            // hardcoded-value for environment.id 4 (should be "WebAppMobile" in DB)
            // uncomment this once dev is done
            // disconnect();
          }
        }
        break;
      }

      case "401_SUBSCRIPTION_EXPIRED": {
        dispatch(setSubscriptionState("Terminated"));
        break;
      }

      case "207_NEW_SOURCE_MEDIA_CACHE":
      case "501_MEDIA_TRANSCRIPTION_FAILED": {
        const mediaId = eventMessage.targetObjects.find((to) => to.table === "source-media")
          ?.id as number;
        const emitterOrganizationId = eventMessage.targetObjects.find(
          (to) => to.table === "organization",
        )?.id;
        if (Number(emitterOrganizationId) === Number(organization.id)) {
          dispatch(getMedia({ mediaId }));
          dispatch(setUploadHasBeenTranscribed(mediaId));
          if (eventMessage.action === "501_MEDIA_TRANSCRIPTION_FAILED") {
            dispatch(
              setAlert({
                type: "warning",
                msg: t("alerts.transcriptionFailed"),
                subMsg: t("alerts.transcriptionFailedSubtext"),
              }),
            );
          }
        }
        break;
      }

      case "305_UPDATED_PROJECT": {
        dispatch(getAllProjects({ componentId: "projectListComponent" })).then(() => {
          dispatch(setCurrentProjectFromId(Number(eventMessage.targetObjects[0].id)));
        });
        break;
      }

      case "001_HEARTBEAT": {
        lastHeartbeatRef.current = Date.now();
        break;
      }

      case "201_NEW_WEB_RELEASE": {
        checkNewCommit((eventMessage.targetObjects[0] as any).commit_hash);
        break;
      }

      case "304_UPDATED_BUILD_STATUS": {
        dispatch(fetchBuilds({ componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID }));
        dispatch(
          getAllSessionsByProjectGroup({
            componentId: STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID,
          }),
        );
        break;
      }

      case "331_BLOCKADE_LAB_GENERATION_COMPLETED":
      case "332_BLOCKADE_LAB_GENERATION_ERROR": {
        const data = eventMessage.targetObjects?.find((o: any) => o.blockadeLabImageId);
        if (data) dispatch(setBlockadeLabsImagePreview(data));
        break;
      }

      case "307_UPDATE_PROABONO_SUBSCRIPTION": {
        const data = eventMessage.targetObjects[0] as any;
        const orgId = data.id;
        if (Number(orgId) === Number(organization.id)) {
          dispatch(fetchPaEmbedLinks({}));
          dispatch(
            fetchAnonymousSessionsCount({ componentId: ANONYMOUS_SESSION_COUNTER_COMPONENT }),
          );
        }
        break;
      }

      case "309_UPDATED_CONTENT_LINKS": {
        // relink/delink emit this with the changed action ids + impacted scene
        // content(s). Keep the open graph in sync with OTHER users' changes:
        // reload only when an impacted scene/button belongs to the currently
        // loaded graph, and never for our own echo (the acting client already
        // refreshed locally via setShouldRerender).
        if (Number(eventMessage.issuedFromUser) === currentUserId) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const touchesOpenGraph = eventMessage.targetObjects.some(
          (to) =>
            (to.table === "content_scene" || to.table === "content_new_button") &&
            Boolean(graphContents[Number(to.id)]),
        );
        if (!touchesOpenGraph) break;
        if (graphReloadTimerRef.current) clearTimeout(graphReloadTimerRef.current);
        graphReloadTimerRef.current = setTimeout(() => {
          dispatch(setShouldRerender());
        }, 300);
        break;
      }

      default:
        break;
    }
  }

  useEffect(() => {
    if (jwt) {
      dispatch(fetchSubscription({}));
      // dispatch(getEditorWebLastCommit()).then((res) => {
      //   const lastCom = (res as any).payload?.commitHash;
      //   checkNewCommit(lastCom);
      // });
    }
  }, [jwt]);

  useEffect(() => {
    // const appIsInLandscapeMode = window.matchMedia("(orientation: landscape)");
    // setLandscapeMode(appIsInLandscapeMode.matches);

    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenSize.height]);

  const lstorage = localStorage.getItem("wixar.auth.jwt");
  useEffect(() => {
    if (isStringNullOrEmpty(lstorage)) {
      dispatch(logoutAuth());
    }
  }, [lstorage]);

  const renderTheAppropriateRouter = () => {
    /* Public download page — accessible without authentication */
    if (window.location.pathname.startsWith("/download/")) {
      return (
        <Routes>
          <Route path="/download/:token" element={<PublicDownloadPage />} />
        </Routes>
      );
    }

    /* We have several cases to handle:
    - User is not subscribed
    - User is subscribed & hasn't accepted EULA
    - User is subscribed, has accepted EULA and is on Mobile device
    - User is subscribed, has accepted EULA and is on Mobile device
    */

    /* On page refresh, Redux state (selected project group, current project, etc.)
       is lost. Routes with numeric params (e.g. /courses/123/list) would render
       with empty state, firing API calls with invalid IDs → "Server error" alerts.
       This MUST run before any auth check: on the very first render both jwt and
       user are null (auth restores asynchronously), so auth-gated checks are
       skipped and the full route tree renders with empty state.
       By redirecting unconditionally on the first render when the path has numeric
       segments, no child route ever mounts. The ref resets on page refresh (new
       component instance) but persists across in-app navigations. */
    if (!hasHandledRefreshRedirect.current) {
      hasHandledRefreshRedirect.current = true;
      const currentPath = window.location.pathname;
      if (/\/\d+/.test(currentPath) && !currentPath.startsWith("/download/")) {
        return (
          <Routes>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        );
      }
    }

    /* Case: JWT exists but user profile not yet loaded - show nothing to avoid route flash */
    if (jwt && !user) {
      return null;
    }

    /* Case: user is not subscribed */
    if (user && !isSubscriptionRunning) {
      return (
        <>
          <Routes>
            <Route index element={<NoSubscriptionHome />} />
            <Route path="*" element={<Navigate to={""} />} />
          </Routes>
        </>
      );
    }

    if (user && !user?.has_accepted_cgv) {
      return (
        <Routes>
          <Route index element={<EULA />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgottenpassword" element={<ForgottenPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
        </Routes>
      );
    }

    if (user?.role.name === "Enduser") {
      return (
        <ErrorBoundary currentUserId={Number(user?.id)}>
          <Routes>
            {/* Redirect unauthenticated routes to /stats for logged-in endusers */}
            <Route path="/login" element={<Navigate to="/stats" replace />} />
            <Route path="/forgottenpassword" element={<Navigate to="/stats" replace />} />
            <Route path="/reset-password" element={<Navigate to="/stats" replace />} />
            <Route path="/set-password" element={<Navigate to="/stats" replace />} />

            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/stats" replace />} />

              <Route path="profile" element={<ProfileWrapper enduser />}>
                <Route index element={<ProfileMain />} />
                <Route path="me" element={<ProfileMain />} />
                <Route path="organization" element={<Organization />} />
              </Route>

              <Route path="courses" element={<EnduserProjectGroupsList />} />
              <Route path="courses/:id" element={<EnduserProjectsList />}>
                <Route index element={<EnduserProjectsList />} />
                <Route path="list" element={<EnduserProjectsList />} />
                <Route path="todo" element={<EnduserProjectsList />} />
                <Route path="pending" element={<EnduserProjectsList />} />
                <Route path="completed" element={<EnduserProjectsList />} />
              </Route>

              {/* Enduser Stats Routes */}
              <Route path="stats" element={<EnduserStatsLayout />}>
                <Route index element={<EnduserStatsOverviewPage />} />
                <Route path="parcours" element={<EnduserParcoursListView />} />
                <Route path="parcours/:parcoursId" element={<EnduserParcoursDetailView />} />
                <Route
                  path="parcours/:parcoursId/:moduleId"
                  element={<EnduserModuleDetailView />}
                />
                <Route
                  path="parcours/:parcoursId/:moduleId/session/:sessionId"
                  element={<EnduserSessionDetailView />}
                />
                <Route
                  path="parcours/:parcoursId/:moduleId/session/:sessionId/heatmap"
                  element={<EnduserGazeHeatmapView />}
                />
                <Route path="comparateur" element={<EnduserComparateurView />} />
              </Route>

              <Route path="download-client" element={<DownloadClient />} />
              <Route path="installations" element={<DeviceManager />} />
              <Route path="/error" element={<ErrorView />} />
              <Route
                path="*"
                element={
                  <main style={{ padding: "1rem" }}>
                    <p>Theres nothing here!</p>
                  </main>
                }
              />
            </Route>
          </Routes>
        </ErrorBoundary>
      );
    } else if (
      /* Case: user is subscribed & on Mobile screen, portrait mode */
      isMobile
      //&& !isInLandscapeMode
    ) {
      return (
        <>
          <ErrorBoundary currentUserId={Number(user?.id)}>
            <Routes>
              <Route path="/login" element={<Login mobile={isMobile} />} />
              <Route path="/forgottenpassword" element={<ForgottenPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              <Route path="/" element={<MobileLayout screenSize={screenSize} />}>
                <Route index element={<MobileDashboard />} />
                <Route path="/upload" element={<MobileUploadSelectedFile />} />
                <Route path="/uploads" element={<MobileUploadsList />} />
              </Route>
            </Routes>
          </ErrorBoundary>
          <ZendeskChat />
        </>
      );
      /* TODO: user is subscribed & on Mobile screen, landscape mode */
    } else {
      /* Case: user is subscribed & on Desktop screen */
      return (
        <>
          <ErrorBoundary currentUserId={Number(user?.id)}>
            <Routes location={location} key={location.pathname}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgottenpassword" element={<ForgottenPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              {/* Story 13.15: Standalone preview page using react-unity-webgl */}
              <Route path="/preview" element={<PreviewPage />} />

              <Route path="/" element={<Layout />}>
                <Route index element={<CreatorDashboard />} />

                <Route path="courses" element={<ProjectGroupsList />} />
                <Route path="courses/:id" element={<ProjectGroupEdit />}>
                  <Route path="list" element={<ProjectsList />} />
                  {/* Story 13.15: Preview opens in external window, route unused but kept for URL structure */}
                  <Route path="preview" element={null} />
                  <Route path="publish" element={<ProjectGroupPublish />} />
                  <Route path="information" element={<ProjectGroupInformation />} />
                  <Route path="medias" element={<ProjectGroupMedias />} />
                  <Route path="publish/new" element={<AskPublicationWrapper />}>
                    <Route path="version" element={<PublishNewVersion />} />
                    <Route path="platforms" element={<PublishSameVersionOnNewPlatformsWrapper />} />
                  </Route>
                  <Route path="team" element={<ProjectGroupCollaborators />} />
                  <Route path="learners" element={<ProjectGroupLearnersWrapper />}>
                    <Route index element={<ProjectGroupLearnersList />} />
                    <Route path="new" element={<ProjectGroupLearnersInvite />} />
                    <Route path=":learnerId" element={<ProjectGroupLearnersUpdate />} />
                  </Route>
                </Route>

                <Route path="courses/new" element={<CreateProjectGroupWrapper />} />
                <Route path="courses/:id/edit" element={<EditProjectGroupNameWrapper />} />
                <Route path="courses/:id/edit-name" element={<EditProjectGroupNameWrapper />} />
                <Route
                  path="courses/:id/edit-thumbnail"
                  element={<EditProjectGroupThumbnailWrapper />}
                />
                <Route path="courses/:id/edit-image" element={<EditProjectGroupImageWrapper />} />
                <Route path="courses/:id/edit-theme" element={<EditProjectGroupThemeWrapper />} />
                <Route path="courses/:id/new" element={<Templates />} />
                <Route path="courses/:id/new/:templateId" element={<CreateProjectWrapper />} />
                <Route
                  path="courses/:id/duplicate/:projectId"
                  element={<DuplicateProjectWrapper />}
                />
                <Route
                  path="courses/:id/duplicateGroup"
                  element={<DuplicateProjectGroupWrapper />}
                />
                <Route path="courses/:id/list/:projectId" element={<Main />}>
                  <Route index element={<ProjectEdit />} />
                  <Route path="graph" element={<ProjectGraph />} />
                  {/* Story 13.15: Preview opens in external window, route unused but kept for URL structure */}
                  <Route path="preview" element={null} />
                  <Route path="medias" element={<ProjectMedias />} />
                  <Route path="information" element={<ProjectInfo />} />
                  <Route
                    path="graph/background-music/edit"
                    element={<BackgroundMusic mode="edit" />}
                  />
                  <Route path="graph/node/:contentType/:nodeId" element={<NodeScreen />} />
                  <Route path="graph/create/scene" element={<CreateNewScene />} />
                  <Route path="learners" element={<ProjectParticipants />} />
                </Route>
                <Route
                  path="courses/:id/list/:projectId/graph/edit-name"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/edit-language"
                  element={<EditProjectLanguageWrapper mode="edit-language" />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/edit-thumbnail"
                  element={<EditProjectThumbnailWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-name"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-language"
                  element={<EditProjectLanguageWrapper mode="edit-language" />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-thumbnail"
                  element={<EditProjectThumbnailWrapper />}
                />
                <Route path="courses/:id/template/:projectId" element={<FaitTemplateWrapper />} />

                <Route path="statistics" element={<StatisticsLayout />}>
                  <Route index element={<GlobalView />} />
                  <Route path="parcours" element={<ParcoursListView />} />
                  <Route path="parcours/:parcoursId" element={<ParcoursDetailView />} />
                  <Route path="parcours/:parcoursId/:moduleId" element={<ModuleView />} />
                  <Route path="apprenants" element={<ApprenantsListView />} />
                  <Route path="apprenants/:userId" element={<ApprenantDetailView />} />
                  <Route path="apprenants/:userId/:parcoursId" element={<ParcoursDetailView />} />
                  <Route path="apprenants/:userId/:parcoursId/:moduleId" element={<ModuleView />} />
                  <Route path="classement" element={<ClassementView />} />
                  <Route path="comparateur" element={<ComparateurView />} />
                  <Route path="reports" element={<ReportsListView />} />
                  <Route path="reports/new" element={<ReportsFormView mode="create" />} />
                  <Route path="reports/:id/edit" element={<ReportsFormView mode="edit" />} />
                  {/* Redirections rétrocompatibilité */}
                  <Route path="group" element={<Navigate to="/statistics" replace />} />
                  <Route
                    path="learners"
                    element={<Navigate to="/statistics/apprenants" replace />}
                  />
                </Route>
                <Route path="profile" element={<ProfileWrapper />}>
                  <Route index element={<ProfileMain />} />
                  <Route path="me" element={<ProfileMain />} />
                  <Route path="organization" element={<Organization />} />
                  <Route path="subscription" element={<Subscription />} />
                </Route>

                <Route path="download-client" element={<DownloadClient />} />
                <Route path="installations" element={<DeviceManager />} />
                {process.env.REACT_APP_HIDE_RELEASES !== "true" && (
                  <Route path="releases" element={<Releases />} />
                )}
                <Route path="help" element={<Help />} />
                <Route path="help-center" element={<HelpCenter />} />
                <Route path="medias" element={<Gallery />} />
                <Route path="chat" element={<ChatWrapper />}>
                  <Route path="scenario-helper" element={<ScenarioHelperChat />} />
                  <Route path="wixarpedia" element={<WixarpediaChat />} />
                </Route>

                <Route path="agents" element={<AgentsWrapper />}>
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="list" element={<AgentList />} />
                  <Route path="documents" element={<DocumentsList />} />
                  <Route path="new" element={<AgentForm />} />
                  <Route path=":id" element={<AgentDetail />} />
                  <Route path=":id/settings" element={<AgentForm />} />
                </Route>

                <Route path="team" element={<CollaboratorsWrapper />}>
                  <Route index element={<CollaboratorsList />} />
                  <Route path="list" element={<CollaboratorsList />} />
                  <Route path=":id" element={<UpdateCollaborator />} />
                  <Route path="new" element={<CreateCollaborator />} />
                  {/* For now there is no CollaboratorsCsvImport
									<Route path="new/invite" element={CreateCollaborator />} />
                  <Route path="new/csv-import" element={<CollaboratorsCsvImport />} /> */}
                </Route>
                <Route path="collaborators" element={<CollaboratorsWrapper />}>
                  <Route index element={<CollaboratorsList />} />
                  <Route path="list" element={<CollaboratorsList />} />
                  <Route path=":id" element={<UpdateCollaborator />} />
                  <Route path="new" element={<CreateCollaborator />} />
                </Route>
                <Route path="learners" element={<ParticipantsWrapper />}>
                  <Route index element={<ParticipantsList />} />
                  <Route path="list" element={<ParticipantsList />} />
                  <Route path=":id" element={<UpdateParticipant />} />
                  <Route path="new" element={<NewParticipants />} />
                  <Route path="new/invite-authenticated" element={<CreateParticipant />} />
                  <Route
                    path="new/invite-simplified"
                    element={<CreateParticipant authType="simplified" />}
                  />
                  <Route path="new/invite" element={<NewParticipants />} />
                  <Route path="new/csv-import" element={<ParticipantsCsvImport />} />
                </Route>
                <Route path="templates" element={<TemplatesWrapper />}>
                  <Route index element={<TemplatesList />} />
                </Route>
                <Route path="templates/:id" element={<SpecificTemplateWrapper />}>
                  <Route index element={<ProjectGraph />} />
                  <Route path="node/:contentType/:nodeId" element={<NodeScreen />} />
                  <Route path="create/scene" element={<CreateNewScene />} />
                  <Route path="background-music/edit" element={<BackgroundMusic mode="edit" />} />
                  <Route path="background-music/view" element={<BackgroundMusic mode="view" />} />
                </Route>
                <Route path="templates/:id/new" element={<FaitTemplateWrapper />} />
                <Route path="themes" element={<ThemesWrapper />}>
                  <Route index element={<ThemesList />} />
                </Route>

                <Route
                  path="*"
                  element={
                    <main style={{ padding: "1rem" }}>
                      <p>Theres nothing here!</p>
                    </main>
                  }
                />
              </Route>

              <Route path="/error" element={<ErrorView />} />
            </Routes>
          </ErrorBoundary>
          <GlobalModals />
          <ZendeskChat />
        </>
      );
    }
  };

  return renderTheAppropriateRouter();
};

export default Router;
